import { WELL_COLOR_CATEGORIES, wellAttributes } from './well-colors';
import type { GroupedWell } from './utils';

/**
 * Filters over the wells the section crosses. The rail offers the same attributes to
 * filter by as to colour by — one vocabulary, asked two questions. A filter narrows
 * which crossings take part; a colour rule paints the ones that do.
 */

/** The attributes a well filter can be written over. */
export const WELL_FILTER_CATEGORIES = WELL_COLOR_CATEGORIES;

/** One filter: an attribute, and the values a crossing must be one of to survive. */
export interface WellFilter {
  id: string;
  attribute: string;
  /** The values kept. Empty means the filter is not narrowing anything. */
  values: string[];
  /** Display labels aligned index-for-index with {@link values}. */
  labels: string[];
  enabled: boolean;
}

/**
 * Whether a crossing satisfies one filter. A filter with no values selected keeps
 * everything rather than nothing — an empty selection is an unanswered question, not
 * a demand that nothing match.
 *
 * @param {GroupedWell} well - The crossing.
 * @param {WellFilter} filter - The filter to apply.
 * @return {boolean} True when the crossing survives.
 */
const satisfies = (well: GroupedWell, filter: WellFilter): boolean => {
  if (!filter.enabled || filter.values.length === 0) {
    return true;
  }
  const value = wellAttributes(well)[filter.attribute];
  // A crossing with no value for the attribute cannot be one of the chosen values,
  // so filtering on an attribute excludes the wells that lack it. That is the same
  // answer the map's own filters give.
  return value !== null && value !== undefined && filter.values.includes(value);
};

/**
 * The crossings that survive every filter — filters compose with AND, which is what
 * stacking them in the rail reads as.
 *
 * @param {Array} wells - The crossings the cut returned.
 * @param {Array} filters - The filters in the rail.
 * @return {Array} The crossings kept.
 */
export const applyWellFilters = (
  wells: GroupedWell[],
  filters: WellFilter[]
): GroupedWell[] => {
  if (filters.length === 0) {
    return wells;
  }
  return wells.filter((well) =>
    filters.every((filter) => satisfies(well, filter))
  );
};
